import './MenuItemCustomizeModal.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGuestCartStore } from '../../guestCart/store/useGuestCartStore';
import { formatMoney, round2, toNumber } from '../../guestCart/utils/money';
import {
  itemColorStyle,
  itemHasBackendImage,
  itemInitialFromLabel,
  resolveItemColor,
} from '../../guestCart/utils/itemVisuals';
import {
  allowsModifierMultiSelect,
  getGroupMaxSel,
  getGroupMinSel,
  groupLimitsUseTotalSum,
  optionAllowsStacking,
  optionQtyBounds,
  pickMultipleDistinctOptions,
} from '../../guestCart/utils/modifierGroupFlags';
import toast from 'react-hot-toast';

const MODIFIER_LIMIT_TOAST_MS = 4000;

function modifierGroupLabel(g) {
  const n = String(g?.name || '').trim();
  return n || 'this add-on';
}

function toastModifierTotalOverMax(g, gmax) {
  const label = modifierGroupLabel(g);
  toast.error(`Maximum is ${gmax} total for “${label}” (every option counts).`, {
    duration: MODIFIER_LIMIT_TOAST_MS,
    id: `mod-sum-${gmax}-${label}`,
  });
}

function toastModifierDistinctCap(g, gmax) {
  const label = modifierGroupLabel(g);
  toast.error(`You can pick at most ${gmax} different options for “${label}”.`, {
    duration: MODIFIER_LIMIT_TOAST_MS,
    id: `mod-distinct-${gmax}-${label}`,
  });
}

function numPrice(v) {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/[^0-9.-]/g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function groupKey(g) {
  return String(g?.uuid ?? g?.id ?? '');
}

function optionKey(o) {
  return String(o?.uuid ?? o?.id ?? '');
}

function optionDisplayName(o) {
  return String(o?.name ?? o?.option_name ?? o?.label ?? '').trim() || 'Option';
}

function firstVariationId(it) {
  const v = it?.variations;
  if (!Array.isArray(v) || !v.length) return null;
  return optionKey(v[0]);
}

function isTruthyPreselect(o) {
  const v = o?.is_preselected;
  if (v === true || v === 1) return true;
  if (typeof v === 'string') return v.trim() === '1' || v.toLowerCase() === 'true';
  return false;
}

function emptyQtyMap(opts) {
  const m = {};
  (Array.isArray(opts) ? opts : []).forEach((o) => {
    const k = optionKey(o);
    if (k) m[k] = 0;
  });
  return m;
}

function distinctPositiveCount(map) {
  return Object.values(map || {}).filter((q) => q > 0).length;
}

function sumPositiveQty(map) {
  return Object.values(map || {}).reduce((s, q) => s + (Number(q) > 0 ? Number(q) : 0), 0);
}

function initialQtyByGroup(it) {
  const groups = Array.isArray(it?.modifier_groups) ? it.modifier_groups : [];
  const state = {};
  groups.forEach((g) => {
    const key = groupKey(g);
    if (!key) return;
    const opts = Array.isArray(g.options) ? g.options : [];
    const map = emptyQtyMap(opts);
    if (!opts.length) {
      state[key] = map;
      return;
    }
    const checkboxMode = pickMultipleDistinctOptions(g);
    const required = Number(g.is_required) === 1;

    if (checkboxMode) {
      opts.forEach((o) => {
        const ok = optionKey(o);
        if (!ok || !isTruthyPreselect(o)) return;
        const { min, max } = optionQtyBounds(o);
        map[ok] = Math.min(max, Math.max(min, min > 0 ? min : 1));
      });
    } else if (required) {
      const first = opts[0];
      const ok = optionKey(first);
      if (ok) {
        const { min, max } = optionQtyBounds(first);
        map[ok] = Math.min(max, Math.max(min, 1));
      }
    }
    state[key] = map;
  });
  return state;
}

function ModifierOptionStepper({ value, min, max, onDec, onInc, disabled }) {
  const atMin = value <= min;
  const atMax = value >= max;
  return (
    <div
      className="mic__opt-stepper"
      role="group"
      aria-label="Option quantity"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button type="button" className="mic__opt-stepper__btn" onClick={onDec} disabled={disabled || atMin} aria-label="Decrease">
        −
      </button>
      <span className="mic__opt-stepper__qty">{value}</span>
      <button type="button" className="mic__opt-stepper__btn" onClick={onInc} disabled={disabled || atMax} aria-label="Increase">
        +
      </button>
    </div>
  );
}

export default function MenuItemCustomizeModal({
  item,
  restaurant,
  comboExtraItems = [],
  itemLabel,
  itemDescription,
  orderingDisabled = false,
  orderBlock = null,
  onOrderingBlocked,
  onClose,
}) {
  const cartActions = useGuestCartStore((s) => s.actions);

  const itemKey = item ? String(item.uuid ?? item.id ?? '') : '';

  const [qty, setQty] = useState(1);
  const [instructions, setInstructions] = useState('');
  const [variationId, setVariationId] = useState(() => firstVariationId(item));
  const [qtyByGroup, setQtyByGroup] = useState(() => initialQtyByGroup(item));
  const [comboCounts, setComboCounts] = useState({});
  const [heroImageBroken, setHeroImageBroken] = useState(false);
  const [brokenExtraImageIds, setBrokenExtraImageIds] = useState(() => new Set());

  const variations = useMemo(() => (Array.isArray(item?.variations) ? item.variations : []), [item]);
  const modifierGroups = useMemo(() => (Array.isArray(item?.modifier_groups) ? item.modifier_groups : []), [item]);

  useEffect(() => {
    if (!item) return;
    setQty(1);
    setInstructions('');
    setVariationId(firstVariationId(item));
    setQtyByGroup(initialQtyByGroup(item));
    setComboCounts({});
    setHeroImageBroken(false);
    setBrokenExtraImageIds(new Set());
  }, [item, itemKey]);

  const baseItemPrice = useMemo(() => numPrice(item?.price), [item]);

  const selectedVariation = useMemo(() => {
    if (!variationId || !variations.length) return null;
    return variations.find((v) => optionKey(v) === variationId) || null;
  }, [variationId, variations]);

  const variantForCart = useMemo(() => {
    if (!selectedVariation) return null;
    const vp = numPrice(selectedVariation.price ?? selectedVariation.sale_price);
    const name = String(selectedVariation.variation ?? selectedVariation.name ?? '').trim();
    return {
      id: optionKey(selectedVariation),
      name: name || 'Option',
      priceDelta: round2(vp - baseItemPrice),
    };
  }, [selectedVariation, baseItemPrice]);

  const modifierAddons = useMemo(() => {
    const addons = [];
    modifierGroups.forEach((g) => {
      const gk = groupKey(g);
      if (!gk) return;
      const opts = Array.isArray(g.options) ? g.options : [];
      const map = qtyByGroup[gk] || {};
      opts.forEach((o) => {
        const ok = optionKey(o);
        const q = Math.max(0, Number(map[ok]) || 0);
        if (q <= 0) return;
        const unit = numPrice(o.price);
        addons.push({
          id: ok,
          name: optionDisplayName(o),
          price: round2(unit * q),
          qty: q,
        });
      });
    });
    return addons;
  }, [modifierGroups, qtyByGroup]);

  const comboAddons = useMemo(() => {
    const out = [];
    comboExtraItems.forEach((ex) => {
      const id = String(ex?.uuid ?? ex?.id ?? '');
      if (!id) return;
      const c = comboCounts[id] || 0;
      if (c <= 0) return;
      const unit = numPrice(ex?.price);
      out.push({
        id: `extra-${id}`,
        name: itemLabel(ex) || 'Extra',
        price: round2(unit * c),
        qty: c,
      });
    });
    return out;
  }, [comboExtraItems, comboCounts, itemLabel]);

  const allAddons = useMemo(() => [...modifierAddons, ...comboAddons], [modifierAddons, comboAddons]);

  const unitTotal = useMemo(() => {
    let t = baseItemPrice + (variantForCart ? toNumber(variantForCart.priceDelta, 0) : 0);
    allAddons.forEach((a) => {
      t += toNumber(a.price, 0);
    });
    return round2(t);
  }, [baseItemPrice, variantForCart, allAddons]);

  const lineTotal = useMemo(() => round2(unitTotal * Math.max(1, qty)), [unitTotal, qty]);

  const validationError = useMemo(() => {
    if (!item) return '';
    if (variations.length && !variationId) return 'Choose an option to continue.';

    for (const g of modifierGroups) {
      const gk = groupKey(g);
      if (!gk) continue;
      const opts = Array.isArray(g.options) ? g.options : [];
      const map = qtyByGroup[gk] || {};
      const req = Number(g.is_required) === 1;
      const gmin = getGroupMinSel(g, req);
      const gmax = getGroupMaxSel(g);

      if (groupLimitsUseTotalSum(g)) {
        const sum = sumPositiveQty(map);
        if (gmin > 0 && sum < gmin) {
          return `Choose at least ${gmin} total for ${g.name || 'this section'}.`;
        }
        if (gmax > 0 && sum > gmax) {
          return `At most ${gmax} total for ${g.name || 'this section'}.`;
        }
      } else {
        const distinct = distinctPositiveCount(map);
        if (gmin > 0 && distinct < gmin) {
          return `Select at least ${gmin} option(s) for ${g.name || 'options'}.`;
        }
        if (gmax > 0 && distinct > gmax) {
          return `Select at most ${gmax} option(s) for ${g.name || 'options'}.`;
        }
      }

      for (const o of opts) {
        const ok = optionKey(o);
        const q = Math.max(0, Number(map[ok]) || 0);
        if (q <= 0) continue;
        const { min: omin, max: omax } = optionQtyBounds(o);
        if (q < omin) {
          return `${optionDisplayName(o)}: select at least ${omin}.`;
        }
        if (q > omax) {
          return `${optionDisplayName(o)}: at most ${omax}.`;
        }
      }
    }
    return '';
  }, [item, variations.length, variationId, modifierGroups, qtyByGroup]);

  const setRadioGroup = useCallback((gk, ok, opts) => {
    setQtyByGroup((prev) => {
      const nextMap = { ...(prev[gk] || emptyQtyMap(opts)) };
      opts.forEach((o) => {
        const k = optionKey(o);
        if (k) nextMap[k] = 0;
      });
      const o = opts.find((x) => optionKey(x) === ok);
      const { min, max } = optionQtyBounds(o || {});
      nextMap[ok] = Math.min(max, Math.max(min, min > 0 ? min : 1));
      return { ...prev, [gk]: nextMap };
    });
  }, []);

  const toggleCheckboxGroup = useCallback((gk, ok, g, opts) => {
    setQtyByGroup((prev) => {
      const map = { ...(prev[gk] || emptyQtyMap(opts)) };
      const cur = Math.max(0, Number(map[ok]) || 0);
      if (cur > 0) {
        map[ok] = 0;
        return { ...prev, [gk]: map };
      }
      const gmax = getGroupMaxSel(g);
      const o = opts.find((x) => optionKey(x) === ok);
      const { min, max } = optionQtyBounds(o || {});
      let start = Math.min(max, Math.max(min, min > 0 ? min : 1));

      if (groupLimitsUseTotalSum(g)) {
        const sum = sumPositiveQty(map);
        const room = gmax > 0 ? gmax - sum : Number.POSITIVE_INFINITY;
        start = Math.min(start, Math.max(0, room));
        if (start < (min > 0 ? min : 1)) {
          toastModifierTotalOverMax(g, gmax);
          return prev;
        }
        const nextSum = sum + start;
        if (gmax > 0 && nextSum > gmax) {
          toastModifierTotalOverMax(g, gmax);
          return prev;
        }
      } else {
        const distinct = distinctPositiveCount(map);
        if (gmax > 0 && distinct >= gmax) {
          toastModifierDistinctCap(g, gmax);
          return prev;
        }
      }

      map[ok] = start;
      return { ...prev, [gk]: map };
    });
  }, []);

  const adjustOptionQty = useCallback((gk, ok, delta, g, opts, requiredGroup, radioMode) => {
    setQtyByGroup((prev) => {
      const map = { ...(prev[gk] || emptyQtyMap(opts)) };
      const cur = Math.max(0, Number(map[ok]) || 0);
      const o = opts.find((x) => optionKey(x) === ok);
      const { min: omin, max: omax } = optionQtyBounds(o || {});
      let next = cur + delta;
      if (next <= 0) {
        if (radioMode && requiredGroup) {
          next = Math.max(omin, 1);
        } else {
          map[ok] = 0;
          return { ...prev, [gk]: map };
        }
      }
      const gmax = getGroupMaxSel(g);
      if (groupLimitsUseTotalSum(g) && gmax > 0) {
        const sumOthers = sumPositiveQty(map) - cur;
        const capForLine = gmax - sumOthers;
        next = Math.min(next, capForLine);
      }
      next = Math.min(omax, Math.max(omin, next));

      if (groupLimitsUseTotalSum(g)) {
        const sumBefore = sumPositiveQty(map);
        const sumAfter = sumBefore - cur + next;
        if (gmax > 0 && sumAfter > gmax) {
          toastModifierTotalOverMax(g, gmax);
          return prev;
        }
      } else {
        if (gmax > 0 && delta > 0 && cur === 0) {
          const distinct = distinctPositiveCount(map);
          if (distinct >= gmax) {
            toastModifierDistinctCap(g, gmax);
            return prev;
          }
        }
      }

      map[ok] = next;
      return { ...prev, [gk]: map };
    });
  }, []);

  const bumpCombo = useCallback((exId) => {
    setComboCounts((prev) => ({
      ...prev,
      [exId]: (prev[exId] || 0) + 1,
    }));
  }, []);

  const decrementCombo = useCallback((exId) => {
    setComboCounts((prev) => {
      const cur = prev[exId] || 0;
      if (cur <= 1) {
        const { [exId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [exId]: cur - 1 };
    });
  }, []);

  const handleAddToCart = () => {
    if (orderingDisabled) {
      if (onOrderingBlocked) {
        onOrderingBlocked();
        return;
      }
      toast.error(orderBlock?.message || 'Ordering is not available right now.');
      return;
    }
    if (!item || !restaurant?.id) {
      toast.error('Unable to add this item.');
      return;
    }
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const res = cartActions.addOrIncLine({
      restaurant: { id: restaurant.id, name: restaurant.name },
      lineInput: {
        itemId: item.uuid || item.id,
        name: itemLabel(item) || 'Item',
        basePrice: baseItemPrice,
        qty: Math.max(1, qty),
        imageUrl: itemHasBackendImage(item) ? item.item_photo_url || '' : '',
        imageColor: resolveItemColor(item),
        imageInitial: itemInitialFromLabel(itemLabel(item) || 'Item'),
        variant: variantForCart,
        addons: allAddons,
        notes: instructions.trim(),
      },
    });

    if (res?.type === 'needs_confirm_replace') {
      toast('Your cart has items from another restaurant. Replace the cart to add this item.', { icon: '⚠️' });
      return;
    }
    if (res?.type === 'ignored') return;

    onClose?.();
    cartActions.openDrawer();
    toast.success('Added to cart');
  };

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  const hasHeroImage = itemHasBackendImage(item) && !heroImageBroken;
  const heroInitial = itemInitialFromLabel(itemLabel(item) || 'Item');
  const markExtraImageBroken = (extraId) => {
    if (!extraId) return;
    setBrokenExtraImageIds((prev) => {
      if (prev.has(extraId)) return prev;
      const next = new Set(prev);
      next.add(extraId);
      return next;
    });
  };

  return createPortal(
    <div className="mic" role="presentation">
      <button type="button" className="mic__backdrop" aria-label="Close" onClick={onClose} />
      <div className="mic__panel" role="dialog" aria-modal="true" aria-labelledby="mic-item-title">
        <div className="mic__hero">
          {hasHeroImage ? (
            <img
              className="mic__hero-img"
              src={item.item_photo_url}
              alt=""
              onError={() => setHeroImageBroken(true)}
            />
          ) : (
            <div className="mic__hero-img mic__hero-img--color" style={itemColorStyle(item)} role="img" aria-label={itemLabel(item) || 'Item'}>
              <span className="mic__hero-initial" aria-hidden="true">
                {heroInitial}
              </span>
            </div>
          )}
          <button type="button" className="mic__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="mic__content">
          <div className="mic__scroll">
            <h2 id="mic-item-title" className="mic__title">
              {(itemLabel(item) || 'Item').toUpperCase()}
            </h2>
            {itemDescription(item) ? <p className="mic__desc">{itemDescription(item)}</p> : null}
            <div className="mic__price">{formatMoney(unitTotal)}</div>

            {variations.length ? (
              <section className="mic__section">
                <div className="mic__section-head">
                  <h3 className="mic__section-title">
                    <span className="mic__section-title-text">CHOOSE OPTION</span>
                  </h3>
                </div>
                <div className="mic__options">
                  {variations.map((v) => {
                    const id = optionKey(v);
                    const label = String(v.variation ?? v.name ?? '').trim() || 'Option';
                    const vp = numPrice(v.price ?? v.sale_price);
                    const delta = round2(vp - baseItemPrice);
                    const priceLabel = delta <= 0 ? 'Free' : `+${formatMoney(delta)}`;
                    const checked = variationId === id;
                    return (
                      <label key={id} className={`mic__row ${checked ? 'is-selected' : ''}`}>
                        <input
                          className="mic__radio"
                          type="radio"
                          name={`variation-${item.uuid || item.id}`}
                          checked={checked}
                          onChange={() => setVariationId(id)}
                        />
                        <span className="mic__row-label">{label}</span>
                        <span className={`mic__row-price ${checked ? 'is-accent' : ''}`}>{priceLabel}</span>
                      </label>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {modifierGroups.map((g) => {
              const gk = groupKey(g);
              if (!gk) return null;
              const opts = Array.isArray(g.options) ? g.options : [];
              if (!opts.length) return null;
              const checkboxMode = pickMultipleDistinctOptions(g);
              const required = Number(g.is_required) === 1;
              const map = qtyByGroup[gk] || emptyQtyMap(opts);
              const gmin = getGroupMinSel(g, required);
              const gmax = getGroupMaxSel(g);
              const useTotalSum = groupLimitsUseTotalSum(g);
              const hintRuleParts = [];
              if (useTotalSum) {
                if (gmin > 0) hintRuleParts.push(`At least ${gmin} in total`);
                if (gmax > 0) hintRuleParts.push(`At most ${gmax} in total`);
              } else if (allowsModifierMultiSelect(g)) {
                if (gmin > 0) hintRuleParts.push(`Pick at least ${gmin}`);
                if (gmax > 0) hintRuleParts.push(`Up to ${gmax} options`);
              }
              const hintTitle = hintRuleParts.length ? hintRuleParts.join(' · ') : 'Selection rules';
              let hint = null;
              if (useTotalSum) {
                if (gmax > 0) hint = `Max ${gmax} total`;
                else if (gmin > 0) hint = `Min ${gmin} total`;
              } else if (allowsModifierMultiSelect(g) && (gmin > 0 || gmax > 0)) {
                if (gmax > 0) hint = `Max ${gmax}`;
                else if (gmin > 0) hint = `Pick ${gmin}+`;
              }

              return (
                <section key={gk} className="mic__section">
                  <div className="mic__section-head">
                    <h3 className="mic__section-title">
                      <span className="mic__section-title-text">{String(g.name || 'OPTIONS').toUpperCase()}</span>
                    </h3>
                    <div className="mic__section-head-badges">
                      {required ? <span className="mic__badge">Required</span> : null}
                      {hint ? (
                        <span className="mic__badge mic__badge--muted" title={hintTitle}>
                          {hint}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="mic__options">
                    {opts.map((o) => {
                      const ok = optionKey(o);
                      const q = Math.max(0, Number(map[ok]) || 0);
                      const checked = q > 0;
                      const p = numPrice(o.price);
                      const unitLabel = p <= 0 ? 'Free' : `+${formatMoney(p)}`;
                      const stack = optionAllowsStacking(o);
                      const { min: omin, max: omax } = optionQtyBounds(o);
                      const sumAll = sumPositiveQty(map);
                      const sumOthers = sumAll - q;
                      const stepperMax =
                        useTotalSum && gmax > 0 ? Math.min(omax, Math.max(omin, gmax - sumOthers)) : omax;
                      const showStepper = stack && checked;
                      const priceLabel = showStepper ? unitLabel : q > 1 ? `${unitLabel} × ${q}` : unitLabel;

                      return (
                        <div key={ok} className={`mic__row mic__row--mod ${checked ? 'is-selected' : ''}`}>
                          <label className="mic__row-control">
                            <input
                              className={checkboxMode ? 'mic__check' : 'mic__radio'}
                              type={checkboxMode ? 'checkbox' : 'radio'}
                              name={checkboxMode ? undefined : `mod-${gk}`}
                              checked={checked}
                              onChange={() => {
                                if (checkboxMode) toggleCheckboxGroup(gk, ok, g, opts);
                                else setRadioGroup(gk, ok, opts);
                              }}
                            />
                            <span className="mic__row-label">{optionDisplayName(o)}</span>
                          </label>
                          <div className="mic__row-trail">
                            <span className={`mic__row-price ${checked ? 'is-accent' : ''}`}>{priceLabel}</span>
                            {showStepper ? (
                              <ModifierOptionStepper
                                value={q}
                                min={checkboxMode ? 0 : omin}
                                max={stepperMax}
                                onDec={() => adjustOptionQty(gk, ok, -1, g, opts, required, !checkboxMode)}
                                onInc={() => adjustOptionQty(gk, ok, 1, g, opts, required, !checkboxMode)}
                              />
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            <section className="mic__section">
              <div className="mic__section-head">
                <h3 className="mic__section-title">
                  <span className="mic__section-title-text">DELIVERY INSTRUCTIONS</span>
                </h3>
              </div>
              <textarea
                className="mic__textarea"
                rows={3}
                placeholder="Delivery instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                aria-label="Delivery instructions"
              />
            </section>

            {comboExtraItems.length ? (
              <section className="mic__section">
                <div className="mic__section-head">
                  <h3 className="mic__section-title">
                    <span className="mic__section-title-text">COMBINATION EXTRAS</span>
                  </h3>
                </div>
                <div className="mic__extras">
                  {comboExtraItems.map((ex) => {
                    const eid = String(ex?.uuid ?? ex?.id ?? '');
                    const hasExtraImage = itemHasBackendImage(ex) && !brokenExtraImageIds.has(eid);
                    const extraInitial = itemInitialFromLabel(itemLabel(ex) || 'Item');
                    const count = comboCounts[eid] || 0;
                    return (
                      <article key={eid} className="mic__extra-card">
                        <div className="mic__extra-media">
                          {hasExtraImage ? (
                            <img
                              src={ex.item_photo_url}
                              alt=""
                              loading="lazy"
                              onError={() => markExtraImageBroken(eid)}
                            />
                          ) : (
                            <div
                              className="mic__extra-img mic__extra-img--color"
                              style={itemColorStyle(ex)}
                              role="img"
                              aria-label={itemLabel(ex) || 'Item'}
                            >
                              <span className="mic__extra-initial" aria-hidden="true">
                                {extraInitial}
                              </span>
                            </div>
                          )}
                          {count > 0 ? (
                            <button type="button" className="mic__extra-remove" aria-label={`Remove ${itemLabel(ex)}`} onClick={() => decrementCombo(eid)}>
                              −
                            </button>
                          ) : null}
                          <button type="button" className="mic__extra-add" aria-label={`Add ${itemLabel(ex)}`} onClick={() => bumpCombo(eid)}>
                            +
                          </button>
                          {count > 0 ? <span className="mic__extra-count">{count}</span> : null}
                        </div>
                        <div className="mic__extra-title">{itemLabel(ex)}</div>
                        {numPrice(ex.price) > 0 ? <div className="mic__extra-price">{formatMoney(numPrice(ex.price))}</div> : null}
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>

          <div className="mic__footer">
            <div className="mic__stepper" role="group" aria-label="Quantity">
              <button type="button" className="mic__step-btn" onClick={() => setQty((qq) => Math.max(1, qq - 1))} aria-label="Decrease quantity">
                −
              </button>
              <span className="mic__step-qty">{qty}</span>
              <button type="button" className="mic__step-btn" onClick={() => setQty((qq) => qq + 1)} aria-label="Increase quantity">
                +
              </button>
            </div>
            <button
              type="button"
              className="mic__add-btn"
              onClick={handleAddToCart}
              disabled={Boolean(validationError)}
              aria-disabled={orderingDisabled || Boolean(validationError)}
            >
              {orderingDisabled
                ? (orderBlock?.title || 'Cannot order')
                : `Add to Cart · ${formatMoney(lineTotal)}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
